import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { ImportJobDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Section } from '../../components/Section';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { relativeTime } from '../../lib/format';
import { importKeys } from '../recipes/queries';
import { importTabs, type ImportTab } from './import-draft';

/** Longest side of the on-screen photo preview, in canvas pixels. */
const PREVIEW_MAX_PX = 1200;

type Tab = ImportTab;

const TAB_LABEL: Record<Tab, string> = {
  url: 'From a link',
  text: 'Paste text',
  photo: 'Photo',
};

const SECTION_TITLE: Record<Tab, string> = {
  url: 'Recipe address',
  text: 'Pasted recipe',
  photo: 'Photo of a recipe',
};

const STATE_LABEL: Record<ImportJobDto['reviewState'], string> = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Rejected',
};

export function ImportPage() {
  const [tab, setTab] = useState<Tab>('url');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const previewCanvas = useRef<HTMLCanvasElement | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const jobs = useQuery({ queryKey: importKeys.list, queryFn: () => api.imports.list() });
  const serverConfig = useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: () => api.auth.config(),
    staleTime: 5 * 60_000,
  });
  const tabs = importTabs(serverConfig.data?.features);
  const activeTab: Tab = tabs.includes(tab) ? tab : 'url';

  // Draw the picked photo straight onto a canvas: decoded pixels only, so no
  // URL built from user input ever reaches the DOM. Formats the browser can't
  // decode (e.g. HEIC on most desktops) simply show no preview.
  useEffect(() => {
    const canvas = previewCanvas.current;
    if (!photo || !canvas || typeof createImageBitmap !== 'function') return;
    let cancelled = false;
    void createImageBitmap(photo)
      .then((bitmap) => {
        if (cancelled) return bitmap.close();
        const scale = Math.min(1, PREVIEW_MAX_PX / Math.max(bitmap.width, bitmap.height));
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
      })
      .catch(() => {
        canvas.width = 0;
        canvas.height = 0;
      });
    return () => {
      cancelled = true;
    };
    // activeTab: the canvas remounts empty when the Photo tab is shown again.
  }, [photo, activeTab]);

  function onPickPhoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPhoto(file);
    setFormError(null);
  }

  const start = useMutation({
    mutationFn: async (): Promise<ImportJobDto> => {
      if (activeTab === 'photo') {
        if (!photo) throw new Error('Choose or take a photo of the recipe first.');
        return (await api.imports.fromPhoto(photo)).job;
      }
      if (activeTab === 'url') return (await api.imports.fromUrl({ url: url.trim() })).job;
      const trimmed = title.trim();
      return (
        await api.imports.fromText({
          text,
          ...(trimmed === '' ? {} : { title: trimmed }),
        })
      ).job;
    },
    onSuccess: async (job) => {
      await qc.invalidateQueries({ queryKey: importKeys.all });
      qc.setQueryData(importKeys.detail(job.id), { job });
      void navigate(`/recipes/import/${job.id}`);
    },
  });

  const discard = useMutation({
    mutationFn: (id: string) => api.imports.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: importKeys.all }),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (activeTab === 'photo') {
      if (!photo) {
        setFormError('Choose or take a photo of the recipe first.');
        return;
      }
    } else if (activeTab === 'url') {
      const value = url.trim();
      if (!/^https?:\/\/\S+$/i.test(value)) {
        setFormError('Enter a full recipe address, starting with https://');
        return;
      }
    } else if (text.trim().length < 10) {
      setFormError('Paste the ingredients and steps — at least a few lines.');
      return;
    }
    setFormError(null);
    start.mutate();
  }

  const tabClass = (active: boolean) =>
    `min-h-11 flex-1 rounded-lg px-3 text-sm font-semibold ${
      active ? 'bg-brand text-white' : 'border border-line bg-surface text-ink'
    }`;

  return (
    <AppShell title="Import a recipe">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          FewerBags reads the recipe, pulls out the ingredients and steps, and shows you a draft to
          check before anything is saved. For a link, the page’s photo is saved with the recipe when
          you approve it, along with a link back to the source.
        </p>

        <div role="group" aria-label="Import source" className="flex gap-2">
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={activeTab === value}
              onClick={() => {
                setTab(value);
                setFormError(null);
              }}
              className={tabClass(activeTab === value)}
            >
              {TAB_LABEL[value]}
            </button>
          ))}
        </div>

        <Section accent="orange" title={SECTION_TITLE[activeTab]}>
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
            {activeTab === 'photo' ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted">
                  The photo is sent to Anthropic’s Claude to read the recipe; nothing is saved until
                  you approve it.
                </p>
                <input
                  ref={photoInput}
                  id="import-photo"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={onPickPhoto}
                />
                {photo ? (
                  <canvas
                    ref={previewCanvas}
                    role="img"
                    aria-label="Selected recipe photo"
                    className="max-h-80 w-full rounded-xl border border-line object-contain"
                  />
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => photoInput.current?.click()}
                  disabled={start.isPending}
                >
                  {photo ? 'Choose a different photo' : 'Take or choose a photo'}
                </Button>
              </div>
            ) : activeTab === 'url' ? (
              <Field
                id="import-url"
                label="Link to the recipe"
                type="url"
                inputMode="url"
                autoComplete="url"
                placeholder="https://example.com/best-chili"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            ) : (
              <>
                <Field
                  id="import-title"
                  label="Title (optional)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                />
                <div className="flex flex-col gap-1">
                  <label htmlFor="import-text" className="text-sm font-medium text-ink">
                    Recipe text
                  </label>
                  <textarea
                    id="import-text"
                    rows={10}
                    maxLength={20000}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={
                      'Ingredients\n1 lb ground beef\n…\n\nInstructions\nBrown the beef…'
                    }
                    className="rounded-lg border border-line bg-surface p-3 text-base text-ink placeholder:text-muted"
                  />
                </div>
              </>
            )}
            {formError ? <Alert tone="error">{formError}</Alert> : null}
            {start.isError ? <Alert tone="error">{messageForError(start.error)}</Alert> : null}
            <Button type="submit" disabled={start.isPending}>
              {start.isPending ? 'Reading the recipe…' : 'Import'}
            </Button>
            {start.isPending && activeTab === 'photo' ? (
              <p className="text-sm text-muted">Reading a photo can take up to a minute.</p>
            ) : null}
          </form>
        </Section>

        <Section accent="orange" title="Recent imports">
          {jobs.isError ? <Alert tone="error">{messageForError(jobs.error)}</Alert> : null}
          {jobs.data && jobs.data.jobs.length === 0 ? (
            <p className="text-sm text-muted">Nothing imported yet.</p>
          ) : null}
          <ul className="flex flex-col divide-y divide-line">
            {(jobs.data?.jobs ?? []).map((job) => (
              <li key={job.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">
                    {job.draft?.title ??
                      job.sourceUrl ??
                      (job.draft?.source.ref === 'photo' ? 'Photo of a recipe' : 'Pasted text')}
                  </p>
                  <p className="truncate text-xs text-muted">
                    {STATE_LABEL[job.reviewState]}
                    {job.status === 'failed' ? ' · could not be read' : ''} ·{' '}
                    {relativeTime(job.createdAt)}
                  </p>
                  {job.error ? <p className="text-xs text-fail">{job.error}</p> : null}
                </div>
                <div className="flex shrink-0 gap-2">
                  {job.createdRecipeId ? (
                    <Link
                      to={`/recipes/${job.createdRecipeId}`}
                      className="inline-flex min-h-11 items-center rounded-lg border border-line px-3 text-sm font-semibold text-brand"
                    >
                      Open recipe
                    </Link>
                  ) : (
                    <Link
                      to={`/recipes/import/${job.id}`}
                      className="inline-flex min-h-11 items-center rounded-lg border border-line px-3 text-sm font-semibold text-brand"
                    >
                      Review
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => discard.mutate(job.id)}
                    disabled={discard.isPending}
                    aria-label={`Delete import ${job.draft?.title ?? job.id}`}
                    className="min-h-11 rounded-lg border border-line px-3 text-sm text-fail disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {discard.isError ? <Alert tone="error">{messageForError(discard.error)}</Alert> : null}
        </Section>
      </div>
    </AppShell>
  );
}
